"use strict";
import { QueryClient } from "@tanstack/react-query";
import { binanceWallet } from "components/Web3Provider/binanceWallet";
import { injectedWithFallback } from "components/Web3Provider/injectedWithFallback";
import { topazWalletConnect } from "components/Web3Provider/walletConnect";
import { getChainInfo } from "features/chains/chainInfo";
import { ALL_CHAIN_IDS, UniverseChainId } from "features/chains/types";
import { isTestnetChain } from "features/chains/utils";
import { BRIDGE_WAGMI_CHAINS } from "features/bridge/constants/bridgeChains";
import { isPlaywrightEnv } from "utilities/src/environment/env";
import { logger } from "utilities/src/logger/logger";
import { createClient } from "viem";
import { createConfig, fallback, http } from "wagmi";
import { connect } from "wagmi/actions";
import { coinbaseWallet, injected, metaMask, mock, safe } from "wagmi/connectors";
export const orderedTransportUrls = (chain) => {
  const orderedRpcUrls = [
    ...chain.rpcUrls.interface?.http ?? [],
    ...chain.rpcUrls.default?.http ?? [],
    ...chain.rpcUrls.public?.http ?? [],
    ...chain.rpcUrls.fallback?.http ?? []
  ];
  return Array.from(new Set(orderedRpcUrls.filter(Boolean)));
};
const baseConnectors = [
  injectedWithFallback(),
  binanceWallet(),
  topazWalletConnect(),
  metaMask(),
  coinbaseWallet({ appName: "Topaz Dex", appLogoUrl: `${window.location.origin}/logo.png` }),
  safe()
];
const getDebugWalletAddress = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlWallet = urlParams.get("debugWallet");
  if (urlWallet && isHexAddress(urlWallet)) {
    return urlWallet;
  }
  const localWallet = localStorage.getItem("DEBUG_WALLET");
  if (localWallet && isHexAddress(localWallet)) {
    return localWallet;
  }
  return null;
};
const isHexAddress = (value) => value.startsWith("0x");
const debugWallet = getDebugWalletAddress();
const shouldUseMock = isPlaywrightEnv() || debugWallet;
const mockConnector = shouldUseMock ? mock({
  features: {},
  accounts: [debugWallet || "0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266"]
}) : null;
const connectors = shouldUseMock && mockConnector ? [...baseConnectors, mockConnector] : baseConnectors;
export const PRIVY_DEFAULT_CHAIN = getChainInfo(UniverseChainId.Mainnet);
export const PRIVY_SUPPORTED_CHAINS = [PRIVY_DEFAULT_CHAIN];
const registeredChains = [
  ...ALL_CHAIN_IDS.map(getChainInfo),
  ...BRIDGE_WAGMI_CHAINS
].reduce((acc, chain) => {
  if (!acc.some((existing) => existing.id === chain.id)) {
    acc.push(chain);
  }
  return acc;
}, []);
export const wagmiConfig = createConfig({
  chains: registeredChains,
  connectors,
  client({ chain }) {
    return createClient({
      chain,
      batch: { multicall: true },
      pollingInterval: 12e3,
      transport: fallback(
        orderedTransportUrls(chain).map(
          (url) => http(url, { onFetchResponse: (response) => onFetchResponse(response, chain, url) })
        ),
        // Periodically re-rank by latency + reliability so a degraded/dead endpoint demotes itself
        // instead of being hit on every request. Without this, viem stays on whichever URL it picked
        // first and only falls through on failure — producing a steady stream of failed requests.
        { rank: true }
      )
    });
  }
});
const onFetchResponse = (response, chain, url) => {
  if (response.status !== 200) {
    const message = `RPC provider returned non-200 status: ${response.status}`;
    if (isTestnetChain(chain.id)) {
      logger.warn("wagmiConfig.ts", "client", message, {
        extra: {
          chainId: chain.id,
          url
        }
      });
    } else {
      logger.error(new Error(message), {
        extra: {
          chainId: chain.id,
          url
        },
        tags: {
          file: "wagmiConfig.ts",
          function: "client"
        }
      });
    }
  }
};
export const queryClient = new QueryClient();
if (window.Cypress?.eagerlyConnect) {
  connect(wagmiConfig, { connector: injected() });
}
const isEagerlyConnect = !window.location.search.includes("eagerlyConnect=false");
if (mockConnector && isEagerlyConnect) {
  setTimeout(() => {
    connect(wagmiConfig, {
      connector: mockConnector
    });
  }, 1);
}
